'use client'

/**
 * Inert links for lure email HTML rendered inline on admin screens.
 *
 * Admin screens show simulation (SafeAttack / lure) email HTML through
 * `dangerouslySetInnerHTML`. Sanitizing it removes script, but its links stay
 * live, and they point wherever the attack points — often a domain the AI
 * invented (`swift-secure-portal.co`) that anyone may register. On the
 * PhishTrainer round report one click on "Verify Transaction Now" took the
 * admin out of the product to it (P2, 2026-10-06).
 *
 * Spread the result on the element that holds the HTML (the one carrying
 * `dangerouslySetInnerHTML`):
 *
 *     const inertLinks = useInertLinks()
 *     <div {...inertLinks} … />
 *
 * What it does, and why each part:
 *   - A click (plain, ctrl/cmd, shift), a middle click (`auxclick`) or Enter on
 *     a link (`a[href]`, `area[href]`) is default-prevented in the CAPTURE
 *     phase, at React's root listener — before anything on the way down can
 *     stop the event, so no handler between here and the link can let it
 *     through. The event still bubbles, so a card wrapped around a preview
 *     still opens when its preview is clicked.
 *   - A form in the HTML cannot submit (DOMPurify keeps `<form action>`).
 *   - The link keeps its look: `href`, `target`, classes and inline styles are
 *     untouched, so the preview still looks like the email the learner got.
 *   - The destination stays inspectable: each link's `title` names it (with a
 *     line saying the link is disabled here), so hovering shows where it goes
 *     and an admin can still teach "check the link". The email's own `title`
 *     is overwritten on purpose — a lure can set a reassuring one to hide its
 *     real destination. `aria-disabled` tells a screen reader the same thing.
 *
 * Out of reach by design: a person can still right-click → Copy link, or
 * drag the link out, both deliberate acts. Learner surfaces (the /play game,
 * the employee portal, the landing pages) do not use this: link behaviour can
 * be part of the exercise there.
 */
import { useCallback, useEffect, useRef } from 'react'
import type { FormEvent, KeyboardEvent, MouseEvent, SyntheticEvent } from 'react'
import { useTranslation } from '@/lib/i18n'

/** Elements whose activation navigates. */
export const NAVIGATING_LINK_SELECTOR = 'a[href], area[href]'

type Translate = (key: string, params?: Record<string, string | number>) => string

/** Give every link under `root` a title naming its destination, and mark it disabled. */
export function markLinksInert(root: ParentNode, t: Translate): void {
  for (const link of Array.from(root.querySelectorAll(NAVIGATING_LINK_SELECTOR))) {
    const title = t('previewLinks.disabledTitle', { url: link.getAttribute('href') ?? '' })
    if (link.getAttribute('title') !== title) link.setAttribute('title', title)
    if (link.getAttribute('aria-disabled') !== 'true') link.setAttribute('aria-disabled', 'true')
  }
}

/** The link an event happened on, if it is inside the element holding the HTML. */
function linkOf(event: SyntheticEvent<HTMLElement>): Element | null {
  const target = event.target as Node | null
  const element = target instanceof Element ? target : (target?.parentElement ?? null)
  const link = element?.closest(NAVIGATING_LINK_SELECTOR) ?? null
  return link && event.currentTarget.contains(link) ? link : null
}

function stopLinkClick(event: MouseEvent<HTMLElement>): void {
  if (linkOf(event)) event.preventDefault()
}

function stopLinkEnter(event: KeyboardEvent<HTMLElement>): void {
  if (event.key === 'Enter' && linkOf(event)) event.preventDefault()
}

function stopInnerSubmit(event: FormEvent<HTMLElement>): void {
  if (event.target !== event.currentTarget) event.preventDefault()
}

export interface InertLinkProps<T extends HTMLElement> {
  ref: (node: T | null) => void
  onClickCapture: (event: MouseEvent<T>) => void
  onAuxClickCapture: (event: MouseEvent<T>) => void
  onKeyDownCapture: (event: KeyboardEvent<T>) => void
  onSubmitCapture: (event: FormEvent<T>) => void
}

export function useInertLinks<T extends HTMLElement = HTMLDivElement>(): InertLinkProps<T> {
  const { t } = useTranslation()
  const node = useRef<T | null>(null)

  // Titles go on when the element mounts — which, inside a sheet or dialog,
  // can be a later commit than this component's own (the portal mounts its
  // children afterwards) — …
  const ref = useCallback(
    (element: T | null) => {
      node.current = element
      if (element) markLinksInert(element, t)
    },
    [t],
  )
  // … and again after every commit of this component, which is when React
  // replaces the injected HTML.
  useEffect(() => {
    if (node.current) markLinksInert(node.current, t)
  })

  return {
    ref,
    onClickCapture: stopLinkClick,
    onAuxClickCapture: stopLinkClick,
    onKeyDownCapture: stopLinkEnter,
    onSubmitCapture: stopInnerSubmit,
  }
}
